import React, { useState } from "react";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * PromoteMembershipForm — Sub-sprint D-α.
 *
 * Controlled form for `POST /admin/racket-lab/_ops/promote-membership`.
 * The org is pre-selected to the pilot org and disabled in v0 (Track A
 * defence-in-depth). Shop ids are a textarea with one entry per line.
 */
const ALLOWED_ROLES = [
  "admin",
  "staff",
  "shop_manager",
  "operator",
  "racket_technician",
  "head_coach",
];

const ROLES_REQUIRING_ORG = new Set([
  "shop_manager", "operator", "racket_technician", "head_coach",
]);

export const PromoteMembershipForm = ({
  pilotOrgId = "TEAMSOFTWAREINNOVATIONS",
  value,
  onChange,
  disabled = false,
}) => {
  const t = useRacketLabAdminT();
  const [local, setLocal] = useState(
    value || {
      user_id: "",
      role: "shop_manager",
      organisation_id: pilotOrgId,
      shop_ids: [],
    },
  );

  const update = (k, v) => {
    const next = { ...local, [k]: v };
    setLocal(next);
    onChange?.(next);
  };

  const shopsText = (local.shop_ids || []).join("\n");
  const requiresOrg = ROLES_REQUIRING_ORG.has(local.role);

  return (
    <div data-testid="rl-ops-promote-form" className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">{t("ops.promote.form.user_id_label")}</span>
        <input
          data-testid="rl-ops-promote-user-id"
          value={local.user_id}
          onChange={(e) => update("user_id", e.target.value)}
          disabled={disabled}
          placeholder={t("ops.promote.form.user_id_placeholder")}
          className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">{t("ops.promote.form.role_label")}</span>
        <select
          data-testid="rl-ops-promote-role"
          value={local.role}
          onChange={(e) => update("role", e.target.value)}
          disabled={disabled}
          className="rounded-md border border-slate-300 px-2 py-1 text-sm"
        >
          {ALLOWED_ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
        </select>
        <span className="text-[10px] text-slate-500">
          {t("ops.promote.form.role_hint")}
        </span>
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">
          {t("ops.promote.form.org_label_locked")}
          {requiresOrg && <span className="ml-1 text-rose-600">*</span>}
        </span>
        <input
          data-testid="rl-ops-promote-org"
          value={local.organisation_id}
          disabled
          className="rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-sm text-slate-500"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">{t("ops.promote.form.shop_ids_label")}</span>
        <textarea
          data-testid="rl-ops-promote-shops"
          value={shopsText}
          onChange={(e) => update("shop_ids", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
          disabled={disabled}
          rows={3}
          placeholder="shop-uuid-1\nshop-uuid-2"
          className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
        />
      </label>

      <p className="text-[10px] text-slate-500">
        {t("ops.promote.form.footer_note")}
      </p>
    </div>
  );
};

export default PromoteMembershipForm;
